if (!customElements.get("product-configurator-summary-modal")) {
  class ProductConfiguratorSummaryModal extends HTMLElement {
    constructor() {
      super();
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      this.configurator = this.closest("product-configurator");
      this.groupContainer = this.querySelector("[data-product-configurator-summary-groups]");
      this.groupTemplate = this.querySelector("[data-product-configurator-summary-group-template]");
      this.total = this.querySelector("[data-product-configurator-summary-total]");
      this.image = this.querySelector("[data-product-configurator-summary-image]");

      if (!this.dialog || !this.configurator || !this.groupContainer || !this.groupTemplate) return;

      this.trigger = this.configurator.querySelector(
        `[data-product-configurator-summary-open][aria-controls="${this.dialog.id}"]`,
      );
      if (!this.trigger) return;

      this.trigger.addEventListener("click", this.handleTriggerClick);
      this.querySelectorAll("[data-product-configurator-summary-close]").forEach((button) => {
        button.addEventListener("click", () => this.dialog.close());
      });
      this.dialog.addEventListener("click", this.handleDialogClick);
      this.dialog.addEventListener("close", this.handleDialogClose);
      this.dialog.addEventListener("keydown", this.handleDialogKeydown);
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener("click", this.handleTriggerClick);
      this.dialog?.removeEventListener("click", this.handleDialogClick);
      this.dialog?.removeEventListener("close", this.handleDialogClose);
      this.dialog?.removeEventListener("keydown", this.handleDialogKeydown);
    }

    handleTriggerClick(event) {
      event.preventDefault();
      this.returnFocusElement = event.currentTarget;
      this.render();

      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add("product-configurator-summary-modal-open");
      this.querySelector("[data-product-configurator-summary-close]")?.focus();
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    handleDialogClose() {
      if (!document.querySelector("product-configurator-summary-modal dialog[open]")) {
        document.documentElement.classList.remove("product-configurator-summary-modal-open");
      }
      this.returnFocusElement?.focus();
      this.returnFocusElement = null;
    }

    handleDialogKeydown(event) {
      if (event.key === "Escape") event.stopPropagation();
    }

    render() {
      const groups = [
        this.buildNativeOptionsGroup(),
        this.buildFabricGroup(),
        ...this.buildAddonGroups(),
      ].filter(Boolean);

      this.groupContainer.replaceChildren(...groups);
      this.renderImage();

      const footerPrice = this.configurator.querySelector("[data-footer-price]")?.textContent?.trim();
      if (this.total && footerPrice) this.total.textContent = footerPrice;
    }

    buildNativeOptionsGroup() {
      const fields = Array.from(this.configurator.querySelectorAll("[data-pdp-native-option]"));
      const rows = fields.map((field) => {
        const input = field.querySelector("[data-pdp-native-value]:checked");
        if (!input) return null;

        const price = input.closest("label")?.querySelector(".product-configurator__choice-price")?.textContent?.trim();
        return this.createRow(field.dataset.optionName || "", input.value, price || "");
      }).filter(Boolean);

      if (!rows.length) return null;
      const title = fields[0]?.closest("[data-configurator-step]")?.dataset.summaryGroupLabel || "";
      return this.createGroup(title, rows);
    }

    buildFabricGroup() {
      const field = this.configurator.querySelector("[data-pdp-fabrics]");
      const input = field?.querySelector("[data-pdp-fabric]:checked");
      if (!field || !input) return null;

      const familyName = input.dataset.familyName || "";
      const typeName = input.dataset.fabricTypeLabel || "";
      const typeValue = familyName && typeName && familyName.toLowerCase() !== typeName.toLowerCase()
        ? `${familyName} (${typeName})`
        : familyName || typeName;
      const colorValue = input.dataset.fabricColorLabel || input.dataset.fabricName || input.value;
      const familyCard = this.configurator.querySelector(
        `[data-pdp-fabric-family-card][data-family-handle="${this.escapeAttribute(input.dataset.familyHandle || "")}"]`,
      );
      const familyPrice = familyCard?.querySelector("[data-pdp-fabric-family-price]")?.textContent?.trim() ||
        this.formatPriceDifference(0);
      const colorLabel = (this.dataset.fabricColorLabelTemplate || "").replace("__FAMILY__", familyName);
      const rows = [
        this.createRow(this.dataset.fabricTypeLabel || "", typeValue, familyPrice),
        this.createRow(colorLabel, colorValue, this.formatPriceDifference(0)),
      ].filter(Boolean);
      const title = field.closest("[data-configurator-step]")?.dataset.summaryGroupLabel || "";

      return this.createGroup(title, rows);
    }

    buildAddonGroups() {
      return Array.from(this.configurator.querySelectorAll("[data-pdp-addon-group]")).map((field) => {
        const groupHandle = field.dataset.addonGroupHandle || "";
        const control = field.querySelector("select[data-pdp-addon-choice]") ||
          field.querySelector("input[data-pdp-addon-choice]:checked");
        const value = control?.value || "none";
        const choice = this.configurator.getAddonChoice?.(groupHandle, value);

        if (!control || value === "none" || !choice?.variantId) return null;

        const description = String(choice.description || "").trim();
        const resolvedVariant = this.getAddonVariantSummary(choice);
        const selectedValue = [choice.label || control.dataset.addonChoiceLabel || value, resolvedVariant, description]
          .filter(Boolean)
          .join(" ");
        const row = this.createRow(
          this.dataset.addonRowLabel || "",
          selectedValue,
          this.formatPriceDifference(choice.price || 0),
        );
        const title = field.closest("[data-configurator-step]")?.dataset.summaryGroupLabel || choice.groupLabel || "";

        return row ? this.createGroup(title, [row]) : null;
      }).filter(Boolean);
    }

    createGroup(title, rows) {
      if (!title || !rows.length) return null;

      const fragment = this.groupTemplate.content.cloneNode(true);
      const group = fragment.querySelector(".product-configurator-summary-modal__group");
      group.querySelector("[data-summary-group-title]").textContent = title;
      group.querySelector("[data-summary-group-rows]").append(...rows);
      return group;
    }

    createRow(label, value, price) {
      if (!value) return null;

      const row = document.createElement("div");
      const copy = document.createElement("div");
      const labelElement = document.createElement("span");
      const valueElement = document.createElement("span");
      const priceElement = document.createElement("span");

      row.className = "product-configurator-summary-modal__row";
      copy.className = "product-configurator-summary-modal__row-copy";
      labelElement.className = "product-configurator-summary-modal__row-label";
      valueElement.className = "product-configurator-summary-modal__row-value";
      priceElement.className = "product-configurator-summary-modal__row-price";

      labelElement.textContent = this.withColon(label);
      valueElement.textContent = value;
      priceElement.textContent = price;
      copy.append(labelElement, valueElement);
      row.append(copy, priceElement);
      return row;
    }

    renderImage() {
      if (!this.image) return;

      const gallery = document.querySelector("product-gallery");
      const activeImage = gallery?.querySelector("[data-product-gallery-main] .swiper-slide-active:not([hidden]) img") ||
        gallery?.querySelector("[data-gallery-main-slide]:not([hidden]) img");
      const source = activeImage?.currentSrc || activeImage?.src || "";

      if (!source) return;
      this.image.src = source;
      this.image.alt = activeImage.alt || this.image.alt;
      this.image.hidden = false;
    }

    formatPriceDifference(cents) {
      return this.configurator.formatPriceDifference?.(cents) || "";
    }

    getAddonVariantSummary(choice) {
      return this.configurator.getAddonVariantSummary?.(choice) || "";
    }

    withColon(value) {
      const label = String(value || "").trim();
      return label && !label.endsWith(":") ? `${label}:` : label;
    }

    escapeAttribute(value) {
      if (window.CSS?.escape) return CSS.escape(value);
      return String(value).replace(/"/g, '\\"');
    }
  }

  customElements.define("product-configurator-summary-modal", ProductConfiguratorSummaryModal);
}
